import React, { forwardRef, Ref, useEffect, useState } from "react";
import logo from "../../assets/images/footer/whiteLogo.svg";
import footerForm from "../../assets/images/footer/rafiki.svg";
import { Button, Card, Input, Snackbar } from "../../components/atoms";
import { CountrySelector } from "../../components/molecules";
import fbLogo from "../../assets/icons/footer/Rectangle (1).svg";
import linkedinLogo from "../../assets/icons/footer/Rectangle.svg";
import { Link } from "react-router-dom";
interface CountryOption {
  value: string;
  label: string;
  dialCode: string;
}

// Custom SVG Check Icon Component
const CheckCircleSVG: React.FC = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    width="48" 
    height="48" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className="text-[#22c55e]"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

interface FormSuccessModalProps {
  onClose: () => void;
}

const FormSuccessModal: React.FC<FormSuccessModalProps> = ({ onClose }) => {
  const [showConfetti, setShowConfetti] = useState(true);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, []);



  return (
    <>
    
 <div className="fixed inset-0 bg-black/100 z-50 flex items-center justify-center p-4">
        <div className="relative w-full max-w-md">
          <div className="bg-[#ffffff] rounded-xl shadow-lg overflow-hidden relative z-10">
            <div className="bg-[#22c55e] h-2 w-full"></div> 
            <div className="p-6 sm:p-8">
              <div className="flex justify-center mb-6">
                <div className="rounded-full bg-[#dcfce7] p-3">
                  <CheckCircleSVG />
                </div>
              </div>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-gray-800 mb-3">
                  Form Submitted Successfully!
                </h1>
                <p className="text-gray-600 mb-6">
                  Thank you for your submission. We have received your information and will process it shortly.
                </p>  
                <div className="bg-[#eff6ff] rounded-lg p-4 text-left">
                  <h3 className="font-medium text-[#1E40AF] mb-2">Next Steps</h3>
                  <ul className="text-sm text-[#1D4ED8] space-y-2">
                 
                    <li>Our team will verify your details within 48 hours and contact you via email/call</li>
                  </ul>
                </div>
              </div>
              <div className="flex justify-center">
                <Button  className="text-[#FFFFFF] bg-[#015cfd] text-white text-[16px] font-normal"   onClick={onClose}>
                  Close
                </Button>     
              </div>
            </div>
          </div>       
        </div>
        {showConfetti && (
          <div className="fixed top-0 left-0 w-full h-full pointer-events-none z-0">
            {Array.from({ length: 50 }).map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 bg-blue-500 rounded-full animate-fall"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `-${Math.random() * 20}px`,
                  opacity: 0.7 + Math.random() * 0.3,
                  backgroundColor: ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'][
                    Math.floor(Math.random() * 5)
                  ],
                  animationDuration: `${1 + Math.random() * 1}s`,
                  animationDelay: `${Math.random() * 0.5}s`,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

const Footer = forwardRef<HTMLDivElement>((props, ref: Ref<HTMLDivElement>) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    message: "",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [snackbarType, setSnackbarType] = useState<
    "success" | "error" | "warning" | "info"
  >("info");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(
    null
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const showSnackbar = (type: typeof snackbarType, message: string) => {
    setSnackbarType(type);
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleCountryChange = (country: any) => {
    setSelectedCountry(country);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

     try {
      const trimmedData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        message: formData.message.trim(),
      };

      const phoneWithCountryCode = selectedCountry
        ? `${selectedCountry.dialCode}${trimmedData.phoneNumber}`
        : trimmedData.phoneNumber;

      const payload = {
        ...trimmedData,
        phoneNumber: phoneWithCountryCode,
      };

      const response = await fetch(
        "https://app.solveesy.com/v1/public/enquiry",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setShowSuccessModal(true);
        setFormData({ name: "", email: "", phoneNumber: "", message: "" });
        setSelectedCountry(null);
      } else {
        showSnackbar("error", result.message || "Submission failed");
      }
    } catch (error) {
      showSnackbar("error", "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="px-4 md:px-[80px]" ref={ref}>
        <div className="grid grid-cols-1 gap-8 h-[fit-content] mb-10 md:grid-cols-2 md:gap-6 lg:gap-12"> 
          <div className="mt-11">
            <div className="font-bold text-[31px] md:text-5xl">
              Take The <span className="text-[#015CFD]">First Step</span>
            </div>
            <h6 className="mt-[12px]">
              Fill out the form below and we'll reach out to you within an hour.
              <br /> Let's have a conversation about how we can help you.
            </h6>
            <img className="mt-[45px]" src={footerForm} alt="form visual" />
          </div>

          <div className="l">
            <Card className="h-fit mt-12 shadow-[0px_0px_59.7px_0px_rgba(37,37,37,0.25)]">
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="flex flex-col sm:flex-row gap-4 w-full">
                  <div className="flex-1 space-y-2.5">
                    <label className="font-medium text-black text-lg block">Name</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your name"
                      className="w-full h-[60px]"
                      required
                    />
                  </div>
                  <div className="flex-1 space-y-2.5">
                    <label className="font-medium text-black text-lg block">Email</label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full h-[60px]"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-3 mt-6">
                  <label className="font-medium text-black text-lg block">Phone number</label>
                  <div className="flex flex-col sm:flex-row gap-2 mt-2">
                    <div>
                      <CountrySelector
                        selectedCountry={selectedCountry}
                        onChange={handleCountryChange}
                      />
                    </div>
                    <div className="w-full sm:ml-1">
                      <Input
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        placeholder="Enter your phone number"
                        className="w-full h-[60px]"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2.5">
                  <label className="font-medium text-black text-lg block">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full p-3 rounded-lg bg-[#F2F2F2] h-32 resize-none focus:ring-blue-500"
                    placeholder="Enter your text here..."
                    required
                  ></textarea>
                </div>

                <Button
                  className="w-full h-[75px]  text-[#FFFFFF] mt-8 bg-[#015cfd] hover:bg-[#0149d1] text-[20px]  md:text-[22px] font-bold rounded-[64px]"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer bottom */}
      <div className="border-t-2 bg-[#040815] h-auto min-h-[323px] border-[#D9D9D9] text-sm md:text-md">
        <div className="flex flex-col md:flex-row justify-between gap-y-10 px-8 md:px-0 md:ml-[136px] md:mr-[150px]">
          <div className="mt-12 md:mt-[63px]">
            <img src={logo} alt="Pemant" className="h-[24px] md:h-[30px]" />
            <div className="flex justify-between mt-5 w-[80px]">
              <a href="https://www.facebook.com/profile.php?id=61560591812484">
                <img src={fbLogo} className="cursor-pointer w-6 h-6" />
              </a>
              <a href="https://www.linkedin.com/company/solveasy/">
                <img src={linkedinLogo} className="cursor-pointer w-6 h-6" />
              </a>
            </div>
          </div>
          <div className="flex flex-col space-y-6 mt-12 md:mt-[98px] mb-8 md:mb-0">
       {[
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
].map((mainLink, i) => (
  <div key={i} className="flex flex-row space-x-4">
    <Link
      to={mainLink.to}
      className="text-sm text-[#999] font-semibold w-24 hover:text-gray-300 transition-colors"
    >
      {mainLink.label}
    </Link>

    {i === 0 && (
      <Link
        to="/legal"
        className="text-sm text-[#999] font-semibold hover:text-gray-300 transition-colors"
      >
        Legal
      </Link>
    )}

    {i === 1 && (
      <Link
        to="/terms-of-use"
        className="text-sm text-[#999] font-semibold hover:text-gray-300 transition-colors"
      >
        Terms of Use
      </Link>
    )}

    {i === 2 && (
      <Link
        to="/privacy-policy"
        className="text-sm text-[#999] font-semibold hover:text-gray-300 transition-colors"
      >
        Privacy Policy
      </Link>
    )}
  </div>
))}
          </div>
        </div>
      </div>
      
      <Snackbar
        isOpen={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        type={snackbarType}
        duration={12000}
      />
   
      {showSuccessModal && (
        <FormSuccessModal onClose={() => setShowSuccessModal(false)} />
      )}

      {/* CSS Animation for confetti */}
      <style>
        {`
          @keyframes fall {
            0% { transform: translateY(0) rotate(0deg); opacity: 1; }
            100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
          }
          .animate-fall {
            animation: fall linear forwards;
          }
        `}
      </style>
    </>
  );
});

export default Footer;